import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSocket } from '@/contexts/SocketProvider';
import { getUserId } from '@/utils/socketClient';
import { getTenantId } from '@/api/endpoints';

const NotificationContext = createContext(null);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { notificationSocket, NOTIFICATION_EVENTS, notificationConnected } = useSocket();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const listenersRef = useRef(new Set());

  // Attach socket listeners once the global notification socket is ready
  useEffect(() => {
    if (!notificationSocket) {
      console.warn('⚠️ [NotificationContext] Notification socket not available yet');
      return;
    }

    const userId = getUserId();
    const tenantId = getTenantId();

    console.log('🔔 [NotificationContext] Setting up notification socket listeners:', { userId, tenantId });

    if (!userId || !tenantId) {
      console.warn('⚠️ [NotificationContext] Missing userId or tenantId', { userId, tenantId });
      return;
    }

    const socket = notificationSocket;

    const onConnect = () => {
      console.log('🔔 [NotificationContext] ✅ Connected to notification server', socket.id);
    };

    const onDisconnect = (reason) => {
      console.log('🔔 [NotificationContext] ❌ Disconnected from notification server:', reason);
    };

    const onReconnect = (attemptNumber) => {
      console.log('🔔 [NotificationContext] 🔄 Reconnected after', attemptNumber, 'attempts');
    };

    const onNewNotification = (notification) => {
      console.log('🔔 [NotificationContext] New notification received:', notification);

      const id = notification?.id || notification?._id;
      if (!notification || !id) {
        console.warn('⚠️ [NotificationContext] Invalid notification format:', notification);
        return;
      }

      const normalized = { ...notification, id };

      setNotifications((prev) => {
        const exists = prev.some((n) => (n.id || n._id) === id);
        if (exists) {
          console.log('⚠️ [NotificationContext] Duplicate notification ignored');
          return prev;
        }
        return [normalized, ...prev];
      });

      setTotalCount((prev) => prev + 1);
      if (!normalized.read) {
        setUnreadCount((prev) => prev + 1);
      }

      listenersRef.current.forEach((listener) => {
        if (typeof listener === 'function') {
          listener(normalized);
        }
      });
    };

    const onNotificationMarkedRead = (data) => {
      console.log('✅ [NotificationContext] Notification marked as read:', data);
      if (data.notificationId) {
        setNotifications((prev) =>
          prev.map((n) =>
            (n.id || n._id) === data.notificationId
              ? { ...n, read: true }
              : n
          )
        );
        setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
      }
    };

    const onError = (error) => {
      console.error('❌ [NotificationContext] Notification socket error:', error);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('reconnect', onReconnect);

    if (socket.connected) {
      console.log('🔔 [NotificationContext] Socket already connected');
    }

    socket.on(NOTIFICATION_EVENTS.NEW_NOTIFICATION, onNewNotification);
    socket.on(NOTIFICATION_EVENTS.NOTIFICATION_MARKED_READ, onNotificationMarkedRead);
    socket.on(NOTIFICATION_EVENTS.ERROR, onError);
    socket.on('error', onError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('reconnect', onReconnect);
      socket.off(NOTIFICATION_EVENTS.NEW_NOTIFICATION, onNewNotification);
      socket.off(NOTIFICATION_EVENTS.NOTIFICATION_MARKED_READ, onNotificationMarkedRead);
      socket.off(NOTIFICATION_EVENTS.ERROR, onError);
      socket.off('error', onError);
      console.log('🔔 [NotificationContext] Cleaned up listeners, but socket remains connected');
    };
  }, [notificationSocket, NOTIFICATION_EVENTS]);

  const addListener = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const updateNotification = useCallback((notificationId, updates) => {
    setNotifications((prev) =>
      prev.map((n) =>
        (n.id || n._id) === notificationId
          ? { ...n, ...updates }
          : n
      )
    );
  }, []);

  const removeNotification = useCallback((notificationId) => {
    setNotifications((prev) => {
      const removed = prev.find((n) => (n.id || n._id) === notificationId);
      if (removed) {
        if (!removed.read) {
          setUnreadCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
        }
        setTotalCount((prevTotal) => (prevTotal > 0 ? prevTotal - 1 : 0));
      }
      return prev.filter((n) => (n.id || n._id) !== notificationId);
    });
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    setTotalCount(0);
  }, []);

  const setInitialNotifications = useCallback((notificationsList, summary) => {
    setNotifications(notificationsList || []);
    setUnreadCount(summary?.unread || 0);
    setTotalCount(summary?.total || notificationsList?.length || 0);
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      totalCount,
      isConnected: notificationConnected,
      addListener,
      updateNotification,
      removeNotification,
      clearAllNotifications,
      setInitialNotifications,
      socket: notificationSocket,
    }),
    [
      notifications,
      unreadCount,
      totalCount,
      notificationConnected,
      addListener,
      updateNotification,
      removeNotification,
      clearAllNotifications,
      setInitialNotifications,
      notificationSocket,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
